import React, { useCallback, useEffect, useRef, useState } from "react";
import { Banner, Button, Checkbox, ModalV2, TextInput } from "@jsq/ds/core";
import { attributeNameConflictErrorMessage } from "./errors";
import * as styles from "./styles_vanilla.css";
import type {
  PromotionLabelConflict,
  PromotionPreflightSummary,
  Space,
  SpaceLocalAttribute,
} from "#common/types/vault";

/** How long a rename sits idle before the pre-check re-runs against it. */
const RENAME_RECHECK_DELAY_MS = 300;

export type PromoteLocalModalProps = {
  /** The space the attribute is being promoted out of. */
  space: Space;
  /** The space-local being promoted; `local.id` is the backend attribute id. */
  local: SpaceLocalAttribute;
  /** Every space, to enrich conflict rows with the clashing local's data type. */
  spaces: Space[];
  /**
   * Server promotion pre-check (DV-499); `label` re-checks a proposed rename.
   * Resolve `null` to fail open (report no conflicts) — the promote mutation
   * remains the enforcement point. Omitted (un-wired callers, stories) the
   * modal skips straight to the confirm step.
   */
  onPreflight?: (
    attributeId: string,
    label?: string,
  ) => Promise<PromotionPreflightSummary | null>;
  /**
   * Confirmed. `newLabel` is set only when the user renamed to clear a conflict.
   *
   * Return a Promise to have the modal await the write: it closes on resolve and
   * stays open, rename intact, on reject — the server still refuses a duplicate
   * label (`ATTRIBUTE_LABEL_CONFLICT`) that the advisory pre-check reported free.
   */
  onPromote: (newLabel?: string) => void | Promise<void>;
  onClose: () => void;
};

const EMPTY_SUMMARY: PromotionPreflightSummary = {
  promotable: true,
  blockers: [],
  conflicts: [],
};

const BLOCKER_REASONS: Record<string, string> = {
  NOT_SPACE_LOCAL: "This attribute is not space-local, so it cannot be promoted.",
  DEPRECATED: "This attribute is deprecated and cannot be promoted.",
  EMPTY_PICKLIST:
    "This attribute is a picklist with no values; add options before promoting it to the global library.",
};

/** The clashing local's "data type · values" line, when it can be resolved client-side. */
function conflictSummary(
  conflict: PromotionLabelConflict,
  spaceId: string,
  spaces: Space[],
): string | null {
  const sp = spaces.find((s) => s.id === spaceId);
  const loc = sp?.locals.find((l) => l.id === conflict.attributeId);
  if (!loc) return null;
  return loc.options && loc.options.length
    ? `${loc.dataType} · ${loc.options.join(", ")}`
    : loc.dataType;
}

/**
 * The promotion pre-check flow (DV-499): before a steward promotes a
 * space-local attribute to global, surface every other holder of the same
 * name grouped by space, let the steward rename their way out of the clash,
 * and gate the confirm CTA on an explicit acknowledgement. Dismissing never
 * promotes. Mirrors the `PromoteFlow` component in the "Doc Vault (Current
 * Design)" Claude Design project.
 */
export function PromoteLocalModal({
  space,
  local,
  spaces,
  onPreflight,
  onPromote,
  onClose,
}: PromoteLocalModalProps) {
  const [summary, setSummary] = useState<PromotionPreflightSummary | null>(null);
  // The label `summary` actually describes. A summary must never be read
  // against a name the user has since edited (the re-check is debounced).
  const [checkedLabel, setCheckedLabel] = useState<string | null>(null);
  // The last check failed or hit an unknown id, so we failed open: no
  // conflicts to report, but nothing verified either. Distinct from "clean".
  const [unverified, setUnverified] = useState(false);
  const [checking, setChecking] = useState(!!onPreflight);
  const [ack, setAck] = useState(false);
  const [newName, setNewName] = useState(local.name);
  // The rename field re-checks only once the user has actually edited it.
  const [edited, setEdited] = useState(false);
  // In-flight promote: holds the CTA so it cannot be double-fired.
  const [submitting, setSubmitting] = useState(false);
  // A name the SERVER refused after the pre-check had passed it. Reachable by
  // design: the pre-check fails open, so a transport error reports "no
  // conflicts", and it cannot see locals in spaces this user can't read.
  const [writeError, setWriteError] = useState<string | null>(null);
  // Discards stale responses: only the latest issued check may apply its result.
  const checkToken = useRef(0);

  const runCheck = useCallback(
    (label?: string) => {
      if (!onPreflight) {
        setSummary(EMPTY_SUMMARY);
        setCheckedLabel(local.name);
        setChecking(false);
        return;
      }
      const token = ++checkToken.current;
      setChecking(true);
      void onPreflight(local.id, label)
        .catch(() => null)
        .then((result) => {
          if (token !== checkToken.current) return;
          // null = unknown id or transport error — fail open; the mutation
          // still enforces uniqueness (DV-510 / ATTRIBUTE_LABEL_CONFLICT).
          // Tracked as unverified so the confirm step never claims this name
          // was checked and found free (cf. `useAttributeNameConflict`, where
          // null means "nothing to report", not "verified free").
          setUnverified(result === null);
          setSummary(result ?? EMPTY_SUMMARY);
          setCheckedLabel(label ?? local.name);
          setChecking(false);
        });
    },
    [onPreflight, local.id, local.name],
  );

  useEffect(() => {
    runCheck();
    return () => {
      // Unmount invalidates any in-flight check.
      checkToken.current += 1;
    };
  }, [runCheck]);

  const trimmedName = newName.trim();
  const renamed = trimmedName !== "" && trimmedName !== local.name;
  const promoteName = renamed ? trimmedName : local.name;

  // The acknowledgement names the attribute, so consent is name-scoped: any
  // change to the name being promoted invalidates it.
  useEffect(() => {
    setAck(false);
  }, [promoteName]);

  // Re-check once the rename field settles — including a revert to the
  // original name, which must restore that name's conflicts.
  useEffect(() => {
    if (!edited || trimmedName === "") return undefined;
    const timer = setTimeout(
      () => runCheck(renamed ? trimmedName : undefined),
      RENAME_RECHECK_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [edited, renamed, trimmedName, runCheck]);

  const conflicts = summary?.conflicts ?? [];
  const hardBlockers = (summary?.blockers ?? []).filter((b) => b !== "LABEL_CONFLICT");
  const spaceLocalHolders = conflicts.filter((c) => c.kind === "space_local");
  const globalClash = conflicts.some((c) => c.kind !== "space_local");
  const nameEmpty = trimmedName === "";
  // While a rename is still settling, `summary` describes the previous name
  // and says nothing about the one in the box — don't let it gate the CTA.
  const stale = checkedLabel !== null && checkedLabel !== promoteName;
  const blocked =
    checking ||
    stale ||
    submitting ||
    !summary ||
    hardBlockers.length > 0 ||
    conflicts.length > 0 ||
    nameEmpty;
  const ok = !blocked && ack;

  // One holder can be bound to several spaces, and global holders are bound to
  // none — so the holder count is not the space count.
  const conflictSpaceCount = new Set(
    spaceLocalHolders.flatMap((c) => c.spaces.map((s) => s.id)),
  ).size;

  const conflictTitle = () => {
    const name = `“${local.name}”`;
    if (spaceLocalHolders.length === 0) return `${name} is already used in the global library`;
    const spacePart =
      conflictSpaceCount > 0
        ? `${conflictSpaceCount} other ${conflictSpaceCount === 1 ? "space" : "spaces"}`
        : `${spaceLocalHolders.length} other space-local ${
            spaceLocalHolders.length === 1 ? "attribute" : "attributes"
          }`;
    return globalClash
      ? `${name} is already used in the global library and in ${spacePart}`
      : `${name} is also a space-local in ${spacePart}`;
  };

  // Renaming is the only way through a global clash — the steward can't rename
  // the global from here, so don't offer the "rename it in each space" route.
  const conflictDescription = globalClash
    ? "A global attribute must be unique across the workspace, and this name is already taken in the global library. Rename the attribute you're promoting to continue."
    : "A global attribute must be unique across the workspace. Promoting this name now would collide with the space-local attributes below. Rename the attribute you're promoting to continue — or, if you'd rather keep this name, navigate to each space below and rename its attribute there before promoting.";

  const renameField = (hint: React.ReactNode) => (
    <div className={styles.field}>
      <TextInput
        label="Rename the attribute you're promoting"
        value={newName}
        placeholder="New global attribute name…"
        isFullWidth
        // Frozen while the promotion is in flight, for the same reason the
        // Display name field is: the refusal names the label that was SENT, and
        // it arrives after any `onChange` clear, so a field that moved
        // underneath it would show an error against a name nobody promoted.
        disabled={submitting}
        onChange={(e) => {
          setNewName(e.target.value);
          setEdited(true);
          // The refusal named the submitted text; it says nothing about this one.
          setWriteError(null);
        }}
      />
      {globalClash && !stale ? (
        <div className={styles.fieldError} role="alert">
          A global attribute is already named “{checkedLabel}”.
        </div>
      ) : writeError ? (
        // The pre-check passed this name and the write still came back refused,
        // so the server's verdict is the more recent truth — show it over the hint.
        <div className={styles.fieldError} role="alert">
          {writeError}
        </div>
      ) : (
        <div className={styles.help}>{hint}</div>
      )}
    </div>
  );

  const submit = async () => {
    if (!ok || submitting) return;
    setSubmitting(true);
    try {
      const result = onPromote(renamed ? trimmedName : undefined);
      if (result instanceof Promise) await result;
      // Only on success. A rejection leaves the modal mounted with the rename
      // still in the field, so the steward can adjust it and retry (DV-648).
      onClose();
    } catch (error) {
      // A refused NAME belongs under the rename field, where the fix is, rather
      // than in a toast (DV-511). Anything else is the container's to report.
      setWriteError(attributeNameConflictErrorMessage(promoteName, error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalV2 show onHide={onClose} size="medium" title="Promote to global attribute" isCentered>
      <ModalV2.Body>
        {/* Only the initial check swaps the body. A rename re-check keeps the
            input mounted (and the user's focus) and just holds the CTA — the
            field follows the steward into the confirm step once a rename has
            started, so a clean-but-mistyped name is still correctable. */}
        {!summary ? (
          <div className={styles.promoChecking}>Checking for same-named attributes…</div>
        ) : hardBlockers.length > 0 ? (
          <Banner
            variant="warning"
            title="This attribute can’t be promoted"
            description={hardBlockers
              .map((b) => BLOCKER_REASONS[b] ?? `Promotion blocked: ${b}.`)
              .join(" ")}
          />
        ) : conflicts.length > 0 ? (
          <>
            <Banner
              variant="warning"
              title={conflictTitle()}
              description={conflictDescription}
            />
            <div className={styles.promoConflictList}>
              <div className={styles.promoConflictHead}>Name used elsewhere</div>
              {conflicts.flatMap((c) =>
                c.spaces.length === 0 ? (
                  <div className={styles.promoConflictRow} key={c.attributeId}>
                    <div>
                      <div className={styles.promoConflictName}>
                        {c.label}{" "}
                        <span className={styles.promoConflictSpace}>
                          {c.kind === "space_local"
                            ? "not currently assigned to a space"
                            : "in the global library"}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  c.spaces.map((s) => {
                    const sub = conflictSummary(c, s.id, spaces);
                    return (
                      <div className={styles.promoConflictRow} key={`${c.attributeId}_${s.id}`}>
                        <div>
                          <div className={styles.promoConflictName}>
                            {c.label}{" "}
                            <span className={styles.promoConflictSpace}>in {s.name}</span>
                          </div>
                          {sub && <div className={styles.promoConflictSub}>{sub}</div>}
                        </div>
                      </div>
                    );
                  })
                ),
              )}
            </div>
            {renameField(
              <>
                Pick a name that isn’t used by the spaces above. The other spaces keep their own
                “{local.name}”.
              </>,
            )}
          </>
        ) : (
          <>
            {renamed && !unverified && (
              <Banner
                variant="success"
                title={`Promoting as “${trimmedName}”`}
                description={`No more conflicts. The other spaces keep their own “${local.name}” as space-local attributes.`}
              />
            )}
            {unverified && (
              <Banner
                variant="information"
                title="We couldn’t check for same-named attributes"
                description={`The pre-check didn’t complete, so “${promoteName}” hasn’t been verified as free. You can still promote — the server re-checks the name and rejects a duplicate.`}
              />
            )}
            <p className={styles.promoLead}>
              Promoting <strong>{promoteName}</strong> from <strong>{space.name}</strong> adds
              it to the curated global library, where every space can reference it. {space.name}{" "}
              keeps it as a global reference with its current settings, and no other space is
              affected until someone references it.
            </p>
            {/* `writeError` opens the field too: a refused name can only be fixed
                by renaming, so the confirm step has to offer the box even when the
                steward never touched it (a clean pre-check followed by a server
                refusal is the whole DV-511 case). */}
            {(edited || writeError) &&
              renameField(
                <>
                  Still editable — each change is re-checked, and the acknowledgement below
                  resets with it.
                </>,
              )}
            <div className={styles.promoAck}>
              <Checkbox
                id={`promote-ack-${local.id}`}
                checked={ack}
                onChange={() => setAck((a) => !a)}
                label={
                  <span>
                    I understand this adds <strong>{promoteName}</strong> to the shared global
                    library. It can’t be undone from here, though a steward can still rename or
                    refine it afterward.
                  </span>
                }
              />
            </div>
          </>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <div className={styles.modalFooter}>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ok} onClick={() => void submit()}>
            {submitting ? "Promoting…" : "Promote to global"}
          </Button>
        </div>
      </ModalV2.Footer>
    </ModalV2>
  );
}
